'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, Code, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { htmlEscapeContent } from '../../data/html-escape-content';
import Link from 'next/link';

const sampleText = `<!DOCTYPE html>
<html>
<head>
    <title>Sample & Test</title>
</head>
<body>
    <ToolPageIntro title="HTML Escape/Unescape">
                Escape or unescape HTML entities and special characters with our free online tool. Perfect for displaying HTML content safely in web applications.
            </ToolPageIntro>
</body>
</html>`;

export default function HtmlEscapeTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [activeTab, setActiveTab] = useState('escape');
    const [isCopied, setIsCopied] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (input.trim() === '') {
            setOutput('');
            return;
        }

        try {
            if (activeTab === 'escape') {
                const escaped = input
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#39;');
                setOutput(escaped);
            } else {
                const unescaped = input
                    .replace(/&amp;/g, '&')
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'")
                    .replace(/&#x([0-9a-fA-F]+);/g, (match, code) =>
                        String.fromCharCode(parseInt(code, 16))
                    )
                    .replace(/&#(\d+);/g, (match, code) =>
                        String.fromCharCode(parseInt(code, 10))
                    );
                setOutput(unescaped);
            }
        } catch (error) {
            setOutput('Error: Invalid input for unescaping');
        }
    }, [input, activeTab]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The text has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
    };

    const handleSwitch = () => {
        setInput(output);
        setActiveTab(prev => (prev === 'escape' ? 'unescape' : 'escape'));
    };

    const loadSample = () => {
        setInput(sampleText);
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    HTML Escape / Unescape
                </h1>
                <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
                    Convert special characters to HTML entities or decode them back to readable text
                </p>
            </div>

            <div className="w-full max-w-6xl mx-auto mb-6">
                <Tabs
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className="w-full"
                >
                    <TabsList className="w-full max-w-md mx-auto grid grid-cols-2 h-12 bg-muted/50 backdrop-blur-sm">
                        <TabsTrigger
                            value="escape"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                        >
                            <Code className="h-4 w-4 mr-2 text-emerald-500 group-data-[state=active]:animate-pulse" />
                            Escape
                        </TabsTrigger>
                        <TabsTrigger
                            value="unescape"
                            className="data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-primary relative group"
                        >
                            <Eye className="h-4 w-4 mr-2 text-blue-500 group-data-[state=active]:animate-pulse" />
                            Unescape
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-4 items-stretch">
                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'escape' ? 'Original HTML' : 'Escaped HTML'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'escape'
                                            ? 'Enter your HTML to escape special characters'
                                            : 'Paste escaped HTML to convert back to normal'}
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        activeTab === 'escape'
                                            ? 'e.g., <div class="container">Hello & Welcome!</div>'
                                            : 'e.g., &lt;div class=&quot;container&quot;&gt;Hello &amp; Welcome!&lt;/div&gt;'
                                    }
                                    className="min-h-[300px] font-mono text-base border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-2 flex items-center justify-center my-2 lg:my-0">
                    <div className="relative w-full h-full flex items-center justify-center">
                        <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent -z-10" />
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={handleSwitch}
                            className="rounded-full w-14 h-14 shadow-lg hover:shadow-primary/20 hover:bg-primary/5 transition-all duration-300 bg-background border-2 border-primary/20 hover:border-primary/50 flex-shrink-0"
                            title="Switch between escape/unescape"
                        >
                            <RefreshCw className="h-6 w-6 text-primary" />
                        </Button>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-5 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        {activeTab === 'escape' ? 'Escaped HTML' : 'Decoded HTML'}
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        {activeTab === 'escape'
                                            ? 'Your HTML with escaped characters'
                                            : 'Your unescaped HTML'}
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!output}
                                    className={cn(
                                        'gap-1.5 transition-all',
                                        isCopied
                                            ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                            : 'hover:bg-primary/5'
                                    )}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[300px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-base border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none'
                                    )}
                                    placeholder={
                                        activeTab === 'escape'
                                            ? 'Escaped HTML will appear here...'
                                            : 'Decoded HTML will appear here...'
                                    }
                                />
                                {!output && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            {activeTab === 'escape'
                                                ? 'Your escaped HTML will appear here'
                                                : 'Your decoded HTML will appear here'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={htmlEscapeContent.title}
                    description={htmlEscapeContent.description}
                    content={htmlEscapeContent.content}
                    faqs={htmlEscapeContent.faqs}
                    relatedTools={htmlEscapeContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
